import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock } from 'lucide-react';
import { Button } from '../../../components/Button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../../components/DropdownMenu';
import { cn } from '../../../lib/utils';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface AdminPageHeaderProps {
  title: string;
  subtitle?: string;
  /** Retained for backwards compatibility but inner breadcrumbs are removed per design standard */
  breadcrumbs?: BreadcrumbItem[];
  icon?: React.ReactNode | React.ElementType;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  onRefresh?: () => Promise<void> | void;
  isRefreshing?: boolean;
  lastRefreshed?: Date | null;
  className?: string;
}

const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: '30 seconds', value: 30000 },
  { label: '1 minute', value: 60000 },
  { label: '5 minutes', value: 300000 },
];

export function AdminPageHeader({
  title,
  subtitle,
  icon,
  badge,
  action,
  secondaryAction,
  onRefresh,
  isRefreshing = false,
  lastRefreshed,
  className,
}: AdminPageHeaderProps) {
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(0);
  const [refreshTime, setRefreshTime] = useState<string>('Just now');

  // Handle auto-refresh interval timer
  useEffect(() => {
    if (autoRefreshInterval <= 0 || !onRefresh) return;

    const timer = setInterval(() => {
      onRefresh();
    }, autoRefreshInterval);

    return () => clearInterval(timer);
  }, [autoRefreshInterval, onRefresh]);

  // Compute friendly relative time for "last refreshed"
  useEffect(() => {
    const updateTimeStr = () => {
      if (!lastRefreshed) {
        setRefreshTime('Just now');
        return;
      }
      const diffSecs = Math.floor((Date.now() - lastRefreshed.getTime()) / 1000);
      if (diffSecs < 10) setRefreshTime('Just now');
      else if (diffSecs < 60) setRefreshTime(`${diffSecs}s ago`);
      else if (diffSecs < 3600) setRefreshTime(`${Math.floor(diffSecs / 60)}m ago`);
      else setRefreshTime(`${Math.floor(diffSecs / 3600)}h ago`);
    };

    updateTimeStr();
    const interval = setInterval(updateTimeStr, 10000);
    return () => clearInterval(interval);
  }, [lastRefreshed]);

  const activeOptionLabel = AUTO_REFRESH_OPTIONS.find((opt) => opt.value === autoRefreshInterval)?.label || 'Off';

  return (
    <div
      className={cn(
        'p-4 rounded-xl border border-border bg-card flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0 shadow-xs',
        className
      )}
    >
      {/* Title & Icon */}
      <div className="flex items-center gap-3 min-w-0">
        {icon && (
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
            {React.isValidElement(icon) ? (
              icon
            ) : (
              (() => {
                const IconComponent = icon as React.ElementType;
                return <IconComponent className="size-4.5" />;
              })()
            )}
          </div>
        )}
        <div className="space-y-0.5 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-page-title font-semibold font-display tracking-tight text-foreground truncate">
              {title}
            </h1>
            {badge}
          </div>
          {subtitle && (
            <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto flex-wrap">
        {/* Last Refreshed & Auto-Refresh dropdown */}
        {onRefresh && (
          <div className="flex items-center border border-border rounded-lg bg-card text-xs text-muted-foreground shadow-xs">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRefresh()}
              disabled={isRefreshing}
              className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground gap-1.5 border-0 rounded-r-none"
              title="Refresh data"
            >
              <RefreshCw className={cn('size-3.5', isRefreshing && 'animate-spin text-primary')} />
              <span className="hidden md:inline font-normal">
                {isRefreshing ? 'Refreshing...' : refreshTime}
              </span>
            </Button>

            <div className="w-[1px] h-4 bg-border" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-[11px] font-normal text-muted-foreground hover:text-foreground border-0 rounded-l-none gap-1"
                >
                  <Clock className="size-3 text-muted-foreground/80" />
                  <span className="hidden lg:inline">{activeOptionLabel}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel className="text-[11px] text-muted-foreground font-medium">
                  Auto-Refresh
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {AUTO_REFRESH_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setAutoRefreshInterval(opt.value)}
                    className={cn(
                      'text-xs cursor-pointer',
                      autoRefreshInterval === opt.value && 'font-semibold text-primary'
                    )}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {secondaryAction}
        {action}
      </div>
    </div>
  );
}
